<template>
  <div>
    <div class="col-6 d-flex align-items-center justify-content-lg-start">
      <Button
        v-if="showBackButton && actionType === 'save' || 'add'"
        :label="$t('List page')"
        size="md"
        icon="ArrowLeftIcon"
        show-icon
        class="mb-1"
        @click="backNavigationAction"
      />
    </div>
    <form-wizard
      ref="loanProduct"
      :title="null"
      :subtitle="null"
      layout="vertical"
      :back-button-text="$t('Previous')"
      :next-button-text="$t('Next')"
      class="wizard-vertical mb-3"
    >

      <!-- General Info tab -->
      <tab-content
        :title="$t('General')"
        :before-change="validateGeneral"
      >
        <validation-observer ref="generalRules">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('General Information') }}
                </h5>
              </b-col>
              <b-col md="6">
                <InputField
                  id="name-of-product"
                  v-model="loanProductFormData.name"
                  :label="$t('Name of Product')"
                  :placeholder="$t('Enter name of product')"
                  type="text"
                  :error-name="$t('Name of Product')"
                  :rules="validationRules"
                  :readonly="isReadonly"
                  onpaste="return false;"
                  :is-allow-special-characters="false"
                />
              </b-col>
              <!-- Product Code -->
              <b-col md="6">
                <InputField
                  id="product-code"
                  v-model.number="loanProductFormData.code"
                  :label="$t('Product Code')"
                  :placeholder="$t('Enter product code')"
                  type="number"
                  :error-name="$t('Product Code')"
                  :rules="isReadonly ? '' : 'required|integer|length:3'"
                  :readonly="isEditProductCode"
                />
              </b-col>
              <!-- Product Code -->

              <!--  Product Category -->
              <b-col md="6">
                <SelectField
                  id="product-category"
                  v-model="loanProductFormData.category"
                  :label="$t('Product Category')"
                  :options="productCategoryOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Product Category')"
                  :rules="validationRules"
                  :reduce="option => option.value"
                  item="label"
                  :set-selected="getPrincipalDueAndUnpaid"
                  :readonly="isReadonly"
                />
              </b-col>
              <!--  Product Category -->
              <!--  Currency -->
              <b-col md="6">
                <SelectField
                  id="currency-code"
                  v-model="loanProductFormData.currencyCode"
                  :reduce="option => option"
                  :label="$t('Currency')"
                  :options="currencyCodeOptions"
                  :placeholder="$t('Select')"
                  :error-name="actionType === 'add' ? $t('Currency') : ''"
                  :rules="actionType === 'add' ? 'required' : ''"
                  item="label"
                  :readonly="isEditProductCode"
                />
              </b-col>
              <!--  Currency -->

            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!-- Loan tab -->
      <tab-content
        :title="$t('Loan Size')"
        :before-change="validateLoanSize"
      >
        <validation-observer ref="loanSizeRules">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Loan Size Information') }}
                </h5>
              </b-col>
              <b-col md="6">
                <SelectField
                  id="limit"
                  v-model="loanProductFormData.loanSize"
                  :label="$t('Limit')"
                  :options="limitOptions"
                  :reduce="option => option.value"
                  :placeholder="$t('Select limit')"
                  :error-name="$t('Limit')"
                  :rules="validationRules"
                  item="label"
                  :set-selected="setLoanSizeAmount"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col
                v-if="isLoanSizeAmount"
                md="12"
              >
                <ValidationObserver>
                  <b-row>
                    <b-col md="6">
                      <InputNumberField
                        id="minimum"
                        v-model="loanProductFormData.minimumLoanAmount"
                        :label="$t('Minimum')"
                        :placeholder="$t('Enter minimum')"
                        :error-name="$t('Minimum')"
                        :rules="isReadonly ? '' : 'required|integer|amount:@maxFee|positive'"
                        :readonly="isReadonly"
                      />
                    </b-col>

                    <b-col md="6">
                      <InputNumberField
                        id="maximum"
                        v-model="loanProductFormData.maximumLoanAmount"
                        :label="$t('Maximum')"
                        :placeholder="$t('Enter maximum')"
                        vid="maxFee"
                        :error-name="$t('Maximum')"
                        :rules="isReadonly ? '' : 'required|integer|positive'"
                        :readonly="isReadonly"
                      />
                    </b-col>
                  </b-row>
                </ValidationObserver>
              </b-col>
            </b-row>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Security Deposit Requirement') }}
                </h5>
              </b-col>

              <b-col md="6">
                <SwitchButton
                  v-model="loanProductFormData.applySecurityDeposit"
                  :label="$t('Hold Fund for Security Deposit')"
                  name="security-deposit-requirement"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <b-row v-if="setSecurityDeposit(loanProductFormData.applySecurityDeposit)">
              <b-col md="6">
                <SelectField
                  id="security-deposit-type"
                  v-model="loanProductFormData.securityDepositType"
                  :label="$t('Security Deposit Type')"
                  :reduce="option => option.value"
                  :options="securityDepositTypeOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Security Deposit Type')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                  :set-selected="setAmountValidation"
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="amount-percentage"
                  v-model.number="loanProductFormData.securityDepositCharge"
                  :label="isPercentageOrAmountLabel"
                  :placeholder="placeholderLabel"
                  type="number"
                  :error-name="isPercentageOrAmountLabel"
                  :rules="isPercentageOrAmount"
                  :readonly="isReadonly"
                />
              </b-col>

              <!-- <b-col md="4">
                <SwitchButton
                  v-model="loanProductFormData.holdFundSecurityDeposit"
                  label="Hold Fund for Security Deposit"
                  name="hold-fund-for-security-deposit"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col> -->
            </b-row>
          </b-form>

        </validation-observer>
      </tab-content>

      <!-- Tenure Information -->
      <tab-content
        :title="$t('Tenure/Principal')"
        :before-change="validateTenure"
      >
        <validation-observer ref="tenureRules">
          <b-form>
            <!-- <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  Tenure Information
                </h5>
              </b-col>
              <b-col md="6">
                <InputField
                  id="grace-period"
                  v-model.number="loanProductFormData.gracePeriod"
                  label="Grace Period (Days)"
                  placeholder="Enter grace period"
                  type="number"
                  error-name="Grace Period"
                  rules="required|integer|positive"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="tenure"
                  v-model.number="loanProductFormData.tenure"
                  label="Tenure (Days)"
                  placeholder="Enter tenure"
                  type="number"
                  error-name="Tenure"
                  rules="required|integer|positive"
                  :readonly="isReadonly"
                />
              </b-col>

            </b-row> -->

            <b-row class="mt-3">
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Principal Repayment Information') }}
                </h5>
              </b-col>
              <b-col md="6">
                <SelectField
                  id="repayment-type"
                  v-model="loanProductFormData.principalRepaymentType"
                  :label="$t('Repayment Type')"
                  :reduce="option => option.value"
                  :options="repaymentTypeOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Repayment Type')"
                  :rules="validationRules"
                  item="label"
                  :set-selected="setRepaymentType"
                  :readonly="isReadonly"
                />
              </b-col>

            </b-row>

            <b-row>
              <b-col md="4">
                <InputField
                  id="principal-to-be-repaid"
                  v-model.number="loanProductFormData.principalToBeRepaidEvery"
                  :label="$t('Principal To Be Repaid Every')"
                  type="number"
                  :error-name="$t('Principal To Be Repaid Every')"
                  :rules="isReadonly ? '' : 'required|integer|positive'"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="4">
                <SelectField
                  id="frequency"
                  v-model="loanProductFormData.principalFrequency"
                  :label="$t('Frequency')"
                  :reduce="option => option.value"
                  :options="frequencyOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Frequency')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="4">
                <InputField
                  id="principal-installment"
                  v-model.number="loanProductFormData.principalInstallment"
                  :label="$t('Over')"
                  type="number"
                  :error-name="$t('Principal Installment')"
                  :rules="isReadonly ? '' : 'required|integer|positive'"
                  :readonly="isReadonly"
                  text="Installments"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!-- Interest Repayment -->
      <tab-content
        :title="$t('Interest Repayment')"
        :before-change="validateInterest"
      >
        <validation-observer ref="interestRules">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Interest Repayment') }}
                </h5>
              </b-col>
              <b-col
                v-if="actionType !== 'view'"
                md="6"
              >
                <SelectField
                  id="applicable-interest"
                  v-model="loanProductFormData.interestCode"
                  :label="$t('Select Applicable Interest')"
                  :reduce="option => option.value"
                  :options="interestApplicationOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Select Applicable Interest')"
                  rules="required"
                  item="label"
                  :set-selected="setApplicableInterest"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col
                v-else
                md="6"
              >
                <SelectField
                  id="applicable-interest"
                  v-model="loanProductFormData.interestCode"
                  :label="$t('Select Applicable Interest')"
                  :reduce="option => option.value"
                  :options="interestApplicationOptions"
                  :placeholder="$t('Select')"
                  item="label"
                  :set-selected="setApplicableInterest"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col>
                <InputField
                  id="applicable-interest-rate"
                  v-model.number="loanProductFormData.interestRate"
                  :label="$t('Applicable Interest Rate')"
                  type="number"
                  rules="custom-maxValue:200|positive"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="repayment-type"
                  v-model="loanProductFormData.interestRepaymentType"
                  :label="$t('Repayment Type')"
                  :reduce="option => option.value"
                  :options="repaymentTypeOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Repayment Type')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>

            <b-row>
              <b-col md="4">
                <InputField
                  id="interest-to-be-repaid"
                  v-model.number="loanProductFormData.interestToBeRepaidEvery"
                  :label="$t('Interest To Be Repaid Every')"
                  type="number"
                  :error-name="$t('Interest To Be Repaid Every')"
                  :rules="isReadonly ? '' : 'required|integer|positive'"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="4">
                <SelectField
                  id="frequency"
                  v-model="loanProductFormData.interestFrequency"
                  :label="$t('Frequency')"
                  :reduce="option => option.value"
                  :options="frequencyOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Frequency')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="4">
                <InputField
                  id="interest-installment"
                  v-model.number="loanProductFormData.interestInstallment"
                  :label="$t('Over')"
                  type="number"
                  :error-name="$t('Interest Installment')"
                  :rules="isReadonly ? '' : 'required|integer|positive'"
                  :readonly="isReadonly"
                  text="Installments"
                />
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <SelectField
                  id="interest-accrual-mode"
                  v-model="loanProductFormData.interestAccrualMode"
                  :label="$t('Interest Accrual Mode')"
                  :reduce="option => option.value"
                  :options="interestAccrualOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Interest Accrual Mode')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <SwitchButton
                  v-model="loanProductFormData.applyDefaultingLoanInterest"
                  :label="$t('Apply Default Loan Interest')"
                  name="apply-loan-fees"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
              <!-- fjffjfjjffjfjjf -->
              <b-col md="6">
                <InputField
                  id="grace-period-default-loan-interest-charges"
                  v-model.number="loanProductFormData.gracePeriodToStartDefaultInDays"
                  :label="$t('Grace Period To Start Default Loan Interest Charges')"
                  :placeholder="$t('Enter grace period to start default loan interest charges')"
                  type="number"
                  :error-name="$t('Grace Period To Start Default Loan Interest Charges')"
                  :rules="isReadonly ? '' : 'required|integer'"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="unpaid-default-loan-interest-account"
                  v-model="loanProductFormData.unpaidDefaultLoanInterestAccount"
                  :label="$t('Unpaid Default Loan Interest Charge Account')"
                  :options="unpaidLoanPenaltyOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Unpaid Default Loan Interest Charge Account')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="suspended-loan-interest-account"
                  v-model="loanProductFormData.suspendedDefaultLoanInterestAccount"
                  :label="$t('Suspended Default Loan Interest Charge Account')"
                  :options="suspendedLoanPenaltyOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Suspended Default Loan Interest Charge Account')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="default-interes-charge-account"
                  v-model="loanProductFormData.defaultLoanInterestIncomeAccount"
                  :label="$t('Default Loan Interest Charge Account')"
                  :reduce="option => option.value"
                  :options="penaltyIncomeOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Default Loan Interest Charge Account')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <!-- fjffjfjjffjfjjf -->
            </b-row>
            <b-row v-if="setApplyDefaultingLoan(loanProductFormData.applyDefaultingLoanInterest)">
              <b-col md="6">
                <SelectField
                  id="default-loan-interest"
                  v-model="loanProductFormData.defaultingLoanInterestChargeDuration"
                  :label="$t('Default Loan Interest Charge Duration')"
                  :reduce="option => option.value"
                  :options="penaltyChargeOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Default Loan Interest Charge Duration')"
                  :rules="validationRules"
                  item="label"
                  :set-selected="setDefaultLoan"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="defaulting-loan-interest-rate"
                  v-model.number="loanProductFormData.defaultingInterestPercentage"
                  :label="$t('Defaulting Loan Interest Rate')"
                  :placeholder="$t('Enter defaulting loan interest rate')"
                  type="text"
                  :error-name="$t('Defaulting Loan Interest Rate')"
                  :rules="isReadonly ? '' : 'required|upTo4Decimal|max-value:100|positive'"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col
                v-if="isDiscontinueDefault"
                md="6"
              >
                <InputField
                  id="discontinue-default-loan"
                  v-model.number="loanProductFormData.discontinueDefaultLoanInterestChargeInDays"
                  :label="$t('Discontinue Default Loan Interest Charge (In Days)')"
                  :placeholder="$t('Enter discontinue default loan interest charge')"
                  type="text"
                  :error-name="$t('Discontinue Default Loan Interest Charge')"
                  :rules="isReadonly ? '' : 'required|positive|integer'"
                  :readonly="isReadonly"
                  @input="setDefaultMessage"
                />
                <small>{{ message }}</small>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!-- Reporting GLs -->
      <tab-content
        :title="$t('Reportings')"
        :before-change="validateReporting"
      >
        <validation-observer ref="reportingRules">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Reporting GLs Accounts') }}
                </h5>
              </b-col>

              <b-col md="6">
                <SelectField
                  id="principal-due-unpaid"
                  v-model="loanProductFormData.principalDueAndUnpaidAccountNumber"
                  :label="$t('Principal Due and Unpaid')"
                  :reduce="option => option.value"
                  :options="principalDueAndUnpaidOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Principal Due and Unpaid')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="interest-receivable"
                  v-model="loanProductFormData.interestReceivableAccountNumber"
                  :label="$t('Interest Receivable')"
                  :options="interestReceivableOptions"
                  :reduce="option => option.value"
                  :placeholder="$t('Select')"
                  :error-name="$t('Interest Receivable')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="interest-due-unpaid"
                  v-model="loanProductFormData.interestDueAndUnpaidAccountNumber"
                  :label="$t('Interest Due and Unpaid')"
                  :options="interestDueAndUnpaidOptions"
                  :reduce="option => option.value"
                  :placeholder="$t('Select')"
                  :error-name="$t('Interest Due and Unpaid')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="interest-suspended"
                  v-model="loanProductFormData.interestSuspenseAccountNumber"
                  :label="$t('Interest Suspended')"
                  :options="interestSuspendedOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Interest Suspended')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="interest-income"
                  v-model="loanProductFormData.interestIncomeAccountNumber"
                  :label="$t('Interest Income')"
                  :options="interestIncomeOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :rules="validationRules"
                  :error-name="$t('Interest Income')"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Provision Reporting GLs') }}
                </h5>
              </b-col>
              <b-col md="6">
                <SelectField
                  id="specific-loan-loss-account"
                  v-model="loanProductFormData.specificLoanLossAccountNumber"
                  :label="$t('Specific Loan Loss Account')"
                  :options="specificLoanLossAccountOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :rules="validationRules"
                  :error-name="$t('Specific Loan Loss Account')"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="general-loan-loss-account"
                  v-model="loanProductFormData.generalLoanLossAccountNumber"
                  :label="$t('General Loan Loss Account')"
                  :options="generalLoanLossAccountOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('General Loan Loss Account')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!--Loan Fee -->
      <tab-content
        :title="$t('Loan Fee')"
        :before-change="validateLoanFee"
      >
        <validation-observer ref="loanFeeRules">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Loan Fee') }}
                </h5>
              </b-col>
              <b-col md="4">
                <SwitchButton
                  v-model="loanProductFormData.applyLoanFees"
                  :label="$t('Apply Loan Fees')"
                  name="apply-loan-fees"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <b-row
              v-if="setLoanFee(loanProductFormData.applyLoanFees)"
              class="align-items-center"
            >
              <b-col md="6">
                <SelectField
                  id="loan-fee"
                  v-model="loanProductFormData.productLoanFees"
                  :label="$t('Loan Fee')"
                  :options="loanFeeOptions"
                  :placeholder="$t('Select loan fee')"
                  :reduce="option => option.value"
                  :error-name="$t('Loan Fee')"
                  :rules="validationRules"
                  item="label"
                  multiple
                  :readonly="isReadonly"
                  class="select-fee"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="unpaid-loan-fee-account"
                  v-model="loanProductFormData.unpaidLoanFeeAccountNumber"
                  :label="$t('Unpaid Loan Fee Account')"
                  :options="unpaidloanFeeOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Unpaid Loan Fee Account')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="suspended-loan-fee-account"
                  v-model="loanProductFormData.suspendedLoanFeeAccountNumber"
                  :label="$t('Suspended Loan Fee Account')"
                  :options="suspendedloanFeeOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Suspended Loan Fee Account')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!--Penalty -->
      <tab-content
        :title="$t('Penalty')"
        :before-change="validatePenalty"
      >
        <validation-observer ref="penaltyRules">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Penalty Information') }}
                </h5>
              </b-col>
              <b-col md="4">
                <SwitchButton
                  v-model="loanProductFormData.applyPenalty"
                  :label="$t('Apply Penalty')"
                  name="apply-penalty"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>

            <b-row
              v-if="setPenalty(loanProductFormData.applyPenalty)"
              class="align-items-center"
            >
              <b-col md="6">
                <SelectField
                  id="penalty"
                  v-model="loanProductFormData.penaltyCode"
                  :label="$t('Penalty')"
                  :options="penaltyOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Penalty')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="unpaid-loan-penalty-account"
                  v-model="loanProductFormData.unpaidLoanPenaltyAccountNumber"
                  :label="$t('Unpaid Loan Penalty Account')"
                  :options="unpaidLoanPenaltyOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Unpaid Loan Penalty Account')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col md="6">
                <SelectField
                  id="suspended-loan-penalty-account"
                  v-model="loanProductFormData.suspendedLoanPenaltyAccountNumber"
                  :label="$t('Suspended Loan Penalty Account')"
                  :options="suspendedLoanPenaltyOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Suspended Loan Penalty Account')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="penalty-income-account"
                  v-model="loanProductFormData.penaltyIncomeAccountNumber"
                  :label="$t('Penalty Income Account')"
                  :reduce="option => option.value"
                  :options="penaltyIncomeOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Penalty Income Account')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="penalty-charge-duration"
                  v-model="loanProductFormData.penaltyChargeDuration"
                  :label="$t('Penalty Charge Duration')"
                  :options="penaltyChargeOptions"
                  :reduce="option => option.value"
                  :placeholder="$t('Select')"
                  error-name="penalty-charge-duration"
                  :rules="validationRules"
                  item="label"
                  :set-selected="setDiscontinuePenalty"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="grace-period"
                  v-model.number="loanProductFormData.penaltyGracePeriod"
                  :label="$t('Grace Period To Start Penalty Charge')"
                  :placeholder="$t('Enter grace period to start  penalty charge')"
                  type="number"
                  :error-name="$t('Grace Period To Start Penalty Charge')"
                  :rules="isReadonly ? '' : 'required|integer'"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col
                v-if="isDiscontinuePenalty"
                md="6"
              >
                <InputField
                  id="discontinue-penalty"
                  v-model.number="loanProductFormData.periodToDiscontinuePenalty"
                  :label="$t('Discontinue Penalty Charge (In Days)')"
                  :placeholder="$t('Enter discontinue penalty charge')"
                  type="number"
                  :error-name="$t('Discontinue Penalty Charge')"
                  :rules="isReadonly ? '' : 'required|integer|positive'"
                  :readonly="isReadonly"
                  @input="setDiscontinuePenaltyMessage"
                />
                <small>{{ penaltyMessage }}</small>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <!--Others -->
      <tab-content
        :title="$t('Other Configuration')"
        :before-change="validateOtherConfigurations"
      >
        <validation-observer ref="otherConfigurationRules">
          <b-form>
            <b-row>
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0">
                  {{ $t('Other Loan Configuration') }}
                </h5>
              </b-col>

              <b-col md="6">
                <SwitchButton
                  v-model="loanProductFormData.allowMultipleActiveLoansPerAccount"
                  :label="$t('Allow Multiple Active Loans per Account')"
                  name="allow-multiple-loans"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <br>
            <b-row>
              <b-col md="6">
                <SelectField
                  id="repayment-option-for-daily-weekly"
                  v-model="loanProductFormData.repaymentOptionForDailyAndWeeklyHoliday"
                  :label="$t('Repayment Option for Daily & Weekly Loan Frequency that fall Due on Weekend/Public holiday')"
                  :options="repaymentOptionForDailyAndWeeklyHolidayOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Repayment Option for Daily & Weekly Loan Frequency that fall Due on Weekend/Public holiday')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="repayment-frequency-holiday"
                  v-model="loanProductFormData.repaymentOptionForOtherFrequencyHoliday"
                  :label="$t('Repayment Option for Other Loan Frequency that fall Due on Weekend/Public holiday')"
                  :options="repaymentOptionForOtherFrequencyHolidayOptions"
                  :placeholder="$t('Select')"
                  :reduce="option => option.value"
                  :error-name="$t('Repayment Option for Other Loan Frequency that fall Due on Weekend/Public holiday')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <b-row class="align-items-center">
              <b-col
                cols="12"
                class="mb-2"
              >
                <h5 class="mb-0 mt-3">
                  {{ $t('Moratorium') }}
                </h5>
              </b-col>
              <b-col md="6">
                <SelectField
                  id="alow-moratorium-loan"
                  v-model="loanProductFormData.allowMoratorium"
                  :label="$t('Allow Moratorium On Loan Repayment')"
                  :reduce="option => option.value"
                  :options="moratoriumTypeOptions"
                  :placeholder="$t('Select')"
                  :error-name="$t('Allow Moratorium On Loan Repayment')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                  :set-selected="setMoratorium"
                />
              </b-col>
            </b-row>

            <b-row v-if="isAllowMoratorium">
              <b-col md="6">
                <SelectField
                  id="moratorium-effect"
                  v-model="loanProductFormData.moratoriumEffect"
                  :label="$t('Moratorium Effect On Loan Duration')"
                  :options="moratoriumEffectOptions"
                  :reduce="option => option.value"
                  :placeholder="$t('Select')"
                  :error-name="$t('Moratorium Effect On Loan Duration')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="moratorium-duration"
                  v-model.number="loanProductFormData.moratoriumDuration"
                  :label="$t('Moratorium Duration')"
                  :placeholder="$t('Enter moratorium duration')"
                  type="number"
                  :error-name="$t('Moratorium Duration')"
                  :rules="isReadonly ? '' : 'required|integer|positive'"
                  :readonly="isReadonly"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="frequency"
                  v-model="loanProductFormData.moratoriumFrequency"
                  :label="$t('Frequency')"
                  :options="moratoriumFrequencyOptions"
                  :reduce="option => option.value"
                  :placeholder="$t('Select')"
                  :error-name="$t('Frequency')"
                  :rules="validationRules"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12">
                <SwitchButton
                  v-model="loanProductFormData.allowDisbursementsToGL"
                  :label="$t('Allow Disbursements To GL')"
                  name="allow-disbursement"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12">
                <SwitchButton
                  v-model="loanProductFormData.enableRepaymentTracker"
                  :label="$t('Enable Replayment Tracker')"
                  name="enable-repayment-tracker"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
              <b-col
                v-if="setRepaymentTracker(loanProductFormData.enableRepaymentTracker)"
                class="align-items-center"
                md="6"
              >
                <InputField
                  id="repayment-trackerInDays"
                  v-model.number="loanProductFormData.repaymentTrackerInDays"
                  :label="$t('Repayment Tracker in Days')"
                  :placeholder="$t('Enter Repayment Tracker in Days')"
                  type="number"
                  :error-name="$t('Repayment Tracker in Days')"
                  :rules="isReadonly ? '' : 'required|integer|positive'"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </tab-content>

      <template slot="finish">
        <div class="wizard-footer-right">
          <Button
            :label="$t('Submit')"
            :loading="isLoading"
            :disabled="isDisabled"
            size="lg"
            @click="handleSubmit"
          />
        </div>

      </template>
      <FullScreenSpinner :show="showSpinner" />
    </form-wizard>

  </div>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import {
  BRow,
  BCol,
  BForm,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapActions } from 'vuex'
import { ValidationObserver } from 'vee-validate'
import { required, maxValue, positive } from '@validations'
import { toastAlert } from '@core/mixins/toast'
import { sweetAlert } from '@core/mixins/sweetAlert'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import SwitchButton from '../../../../components/switch/Switch.vue'
import {
  loanProductCategory,
  repaymentTypes,
  interestAccruals,
  periodPenalties,
  penaltyCharges,
  securityDeposits,
  securityDepositTypes,
  limits,
  repaymentOptionForDailyAndWeeklyHoliday,
  repaymentOptionForOtherFrequencyHoliday,
  moratoriumType,
  moratoriumEffect,
  repaidFrequencies,
} from '../../../../../helpers/data'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import Button from '../../../../components/button/Button.vue'
import { accountPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    FormWizard,
    TabContent,
    BRow,
    BForm,
    BCol,
    Button,
    FullScreenSpinner,
    SwitchButton,
    InputField,
    SelectField,
    InputNumberField,
    ValidationObserver,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  mixins: [toastAlert, sweetAlert],
  data() {
    return {
      required,
      maxValue,
      positive,
      isEditProductCode: false,
      isDisabled: false,
      isLoading: false,
      isAllowMoratorium: false,
      isReadonly: false,
      showSpinner: false,
      showBackButton: false,
      isPercentageOrAmount: 'required|positive',
      isPercentageOrAmountLabel: this.$t('Amount'),
      placeholderLabel: this.$t('Enter amount'),
      loanProductFormData: {
        code: null,
        name: '',
        category: '',
        tenure: '',
        interestReceivableAccountNumber: '',
        interestSuspenseAccountNumber: '',
        principalDueAndUnpaidAccountNumber: '',
        interestDueAndUnpaidAccountNumber: '',
        interestIncomeAccountNumber: '',
        interestAccrualMode: '',
        specificLoanLossAccountNumber: '',
        generalLoanLossAccountNumber: '',
        unpaidLoanFeeAccountNumber: '',
        suspendedLoanFeeAccountNumber: '',
        loanSize: '',
        minimumLoanAmount: null,
        maximumLoanAmount: null,
        applySecurityDeposit: false,
        holdFundSecurityDeposit: false,
        securityDepositType: '',
        securityDepositCharge: null,
        gracePeriod: null,
        principalRepaymentType: '',
        principalFrequency: '',
        principalToBeRepaidEvery: '',
        principalInstallment: '',
        interestRepaymentType: '',
        interestFrequency: '',
        interestToBeRepaidEvery: '',
        interestInstallment: '',
        defaultingInterestPercentage: null,
        applyLoanFees: false,
        productLoanFees: [],
        applyDefaultingLoanInterest: false,

        gracePeriodToStartDefaultInDays: null,
        unpaidDefaultLoanInterestAccount: '',
        suspendedDefaultLoanInterestAccount: '',
        defaultLoanInterestIncomeAccount: '',

        defaultingLoanInterestChargeDuration: '',
        discontinueDefaultLoanInterestChargeInDays: null,
        applyPenalty: false,
        penaltyCode: '',
        penaltyGracePeriod: null,
        penaltyChargeDuration: '',
        periodToDiscontinuePenalty: null,
        penaltyIncomeAccountNumber: '',
        unpaidLoanPenaltyAccountNumber: '',
        suspendedLoanPenaltyAccountNumber: '',
        allowMultipleActiveLoansPerAccount: false,
        allowMoratorium: '',
        moratoriumEffect: '',
        repaymentOptionForDailyAndWeeklyHoliday: '',
        repaymentOptionForOtherFrequencyHoliday: '',
        moratoriumDuration: 0,
        moratoriumFrequency: '',
        enableRepaymentTracker: false,
        repaymentTrackerInDays: '',
        allowDisbursementsToGL: false,
        currencyCode: null,
        interestCode: '',
        interestRate: 0,
      },
      actionType: 'add',
      // productCategoryOptions: loanProductCategory,
      // repaymentTypeOptions: repaymentTypes,
      // frequencyOptions: repaidFrequencies,
      // interestAccrualOptions: interestAccruals,
      periodPenaltyOptions: periodPenalties,
      // penaltyChargeOptions: penaltyCharges,
      securityDepositOptions: securityDeposits,
      // securityDepositTypeOptions: securityDepositTypes,
      // limitOptions: limits,
      principalDueAndUnpaidOptions: [],
      interestReceivableOptions: [],
      interestDueAndUnpaidOptions: [],
      interestSuspendedOptions: [],
      interestIncomeOptions: [],
      specificLoanLossAccountOptions: [],
      generalLoanLossAccountOptions: [],
      loanFeeOptions: [],
      feeDetails: [],
      unpaidloanFeeOptions: [],
      suspendedloanFeeOptions: [],
      loanProduct: [
        { label: 'MicroLoan', id: 18 },
        { label: 'StaffLoan', id: 21 },
        { label: 'SmallAndMediumLoans', id: 20 },
        { label: 'OtherLoans', id: 19 },
        { label: 'HirePurchase', id: 22 },
        { label: 'OnlineLoan', id: 18 },
      ],
      message: '',
      penaltyMessage: '',
      isDiscontinueDefault: false,
      isDiscontinuePenalty: false,
      isInterestRepayment: false,
      isRepaymentType: false,
      penaltyOptions: [],
      suspendedLoanPenaltyOptions: [],
      unpaidLoanPenaltyOptions: [],
      penaltyIncomeOptions: [],
      // repaymentOptionForDailyAndWeeklyHolidayOptions: repaymentOptionForDailyAndWeeklyHoliday,
      // repaymentOptionForOtherFrequencyHolidayOptions: repaymentOptionForOtherFrequencyHoliday,
      // moratoriumTypeOptions: moratoriumType,
      // moratoriumEffectOptions: moratoriumEffect,
      // moratoriumFrequencyOptions: repaidFrequencies,
      isLoanSizeAmount: false,
      permission: '',
      interestApplicationOptions: [],
      currencyCodeOptions: [],
    }
  },
  computed: {
    validationRules() {
      if (this.isReadonly) {
        return ''
      }
      return 'required'
    },
    productCategoryOptions() {
      return loanProductCategory.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    repaymentTypeOptions() {
      return repaymentTypes.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    frequencyOptions() {
      return repaidFrequencies.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    interestAccrualOptions() {
      return interestAccruals.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    penaltyChargeOptions() {
      return penaltyCharges.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    securityDepositTypeOptions() {
      return securityDepositTypes.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    limitOptions() {
      return limits.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    repaymentOptionForDailyAndWeeklyHolidayOptions() {
      return repaymentOptionForDailyAndWeeklyHoliday.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    repaymentOptionForOtherFrequencyHolidayOptions() {
      return repaymentOptionForOtherFrequencyHoliday.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    moratoriumTypeOptions() {
      return moratoriumType.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    moratoriumEffectOptions() {
      return moratoriumEffect.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    moratoriumFrequencyOptions() {
      return repaidFrequencies.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  mounted() {
    const { type, currencyOptions } = this.$route.params
    this.actionType = type
    this.currencyCodeOptions = currencyOptions
    this.getPermission()
    this.getInterestReceivable()
    this.getInterestDueAndUnpaid()
    this.getInterestSuspended()
    this.getInterestIncome()
    this.getSpecificLoanLossAccount()
    this.getGeneralLoanLossAccount()
    this.getLoanFee()
    this.getUnpaidLoanFee()
    this.getSuspendedLoanFee()
    this.fetchPenalties()
    this.getSuspendedLoanPenalty()
    this.getUnpaidLoanPenalty()
    this.getPenaltyIncomeAccount()
    this.getLoanProductDetails()
    this.fetchInterestApplicable()
  },
  methods: {
    ...mapActions({
      getGLAccountsByAccountType: 'account/getGLAccountsByAccountTypeList',
      getGeneralLedger: 'account/getGeneralLedgerList',
      getLoanFees: 'account/getLoanFeeList',
      createLoanProduct: 'account/createLoanProductAction',
      updateLoanProduct: 'account/updateLoanProductAction',
      getLoanProduct: 'account/getProduct',
      getPenalties: 'account/getPenaltiesList',
      getIncomeGLByParentCategory: 'account/getIncomeGLByParentCategory',
      getInterestApplicable: 'account/getInterestList',
    }),
    getPermission() {
      if (this.actionType === 'save' || this.actionType === 'add') {
        this.permission = accountPermissions.CREATE_LOAN_PRODUCT
        return
      }
      if (this.actionType === 'edit') {
        this.permission = accountPermissions.MODIFY_LOAN_PRODUCT
        return
      }
      this.permission = accountPermissions.VIEW_LOAN_PRODUCT
    },
    setMoratorium(selectedValue) {
      if (selectedValue === 'False') {
        this.isAllowMoratorium = false
        this.loanProductFormData.moratoriumEffect = null
        this.loanProductFormData.moratoriumDuration = null
        this.loanProductFormData.moratoriumFrequency = null
      } else {
        this.isAllowMoratorium = true
      }
    },
    setAmountValidation(selectedValue) {
      if (selectedValue === 'PercentageOnLoanAmount') {
        this.isPercentageOrAmount = 'required|max-value:100|positive'
        this.isPercentageOrAmountLabel = this.$t('Percentage')
        this.placeholderLabel = this.$t('Enter percentage')
      } else {
        this.isPercentageOrAmount = 'required|positive'
        this.isPercentageOrAmountLabel = this.$t('Amount')
        this.placeholderLabel = this.$t('Enter amount')
      }
    },
    setDefaultMessage(value) {
      if (value) {
        const text = this.$t('Discontinue default loan interest charge')
        const text2 = this.$t('days after loan expiration')
        this.message = `${text} ${value} ${text2}.`
        return true
      }
      this.message = ''
      return false
    },
    setDiscontinuePenaltyMessage(value) {
      if (value) {
        const text = this.$t('Discontinue penalty charge')
        const text2 = this.$t('days after loan expiration')
        this.penaltyMessage = `${text} ${value} ${text2}.`
        return true
      }
      this.penaltyMessage = ''
      return false
    },
    setPenalty(value) {
      if (!value) {
        this.loanProductFormData.penaltyCode = null
        this.loanProductFormData.penaltyGracePeriod = null
        this.loanProductFormData.penaltyChargeDuration = null
        this.loanProductFormData.periodToDiscontinuePenalty = null
        this.loanProductFormData.penaltyIncomeAccountNumber = null
        return false
      }
      return true
    },
    setApplyDefaultingLoan(value) {
      if (!value) {
        this.loanProductFormData.defaultingLoanInterestChargeDuration = null
        this.loanProductFormData.defaultingInterestPercentage = null
        return false
      }
      return true
    },
    setSecurityDeposit(value) {
      if (!value) {
        this.loanProductFormData.securityDepositType = null
        this.loanProductFormData.securityDepositCharge = null
        this.loanProductFormData.holdFundSecurityDeposit = false
        return false
      }
      return true
    },
    setDefaultLoan(selectedValue) {
      if (selectedValue === 'Discontinuous') {
        this.isDiscontinueDefault = true
        return true
      }
      this.isDiscontinueDefault = false
      this.loanProductFormData.discontinueDefaultLoanInterestChargeInDays = null
      this.message = ''
      return false
    },
    setDiscontinuePenalty(selectedValue) {
      if (selectedValue === 'Discontinuous') {
        this.isDiscontinuePenalty = true
        return true
      }
      this.isDiscontinuePenalty = false
      this.loanProductFormData.periodToDiscontinuePenalty = null
      this.penaltyMessage = ''
      return false
    },
    setLoanFee(value) {
      if (!value) {
        this.loanProductFormData.productLoanFees = null
        this.loanProductFormData.unpaidLoanFeeAccountNumber = null
        this.loanProductFormData.suspendedLoanFeeAccountNumber = null
        return false
      }
      return true
    },
    setRepaymentTracker(value) {
      if (!value) {
        this.loanProductFormData.repaymentTrackerInDays = null
        return false
      }
      return true
    },
    setLoanSizeAmount(value) {
      if (value === 'Limited') {
        this.isLoanSizeAmount = true
        this.loanProductFormData.minimumLoanAmount = null
        this.loanProductFormData.maximumLoanAmount = null
        return false
      }
      this.isLoanSizeAmount = false
      return true
    },
    setRepaymentType(value) {
      if (value === 'Prorated') {
        this.isRepaymentType = true
        this.loanProductFormData.principalFrequency = null
        this.loanProductFormData.principalToBeRepaidEvery = null
        this.loanProductFormData.principalInstallment = null

        return false
      }
      this.isRepaymentType = false
      return true
    },
    setInterestRepayment(value) {
      if (value === 'Prorated') {
        this.isInterestRepayment = true
        this.loanProductFormData.interestFrequency = null
        this.loanProductFormData.interestToBeRepaidEvery = null
        this.loanProductFormData.interestInstallment = null
        return false
      }
      this.isInterestRepayment = false
      return true
    },
    async fetchInterestApplicable() {
      try {
        const response = await this.getInterestApplicable({
          search: { type: 'Debit' },
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = response
        if (status === 200) {
          this.interestApplicationOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({
            label: item.name,
            value: item.code,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async setApplicableInterest(selectedValue) {
      try {
        const response = await this.getInterestApplicable({
          search: { code: selectedValue },
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = await response
        if (status === 200) {
          this.loanProductFormData.interestRate = data.data[0].rate
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async getPrincipalDueAndUnpaid(selectedValue) {
      try {
        const loanProduct = this.loanProduct.find(item => item.label === selectedValue)
        const response = await this.getGLAccountsByAccountType({ accountType: loanProduct.id, permission: this.permission })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.principalDueAndUnpaidOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getInterestReceivable() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 13, permission: this.permission })
        const { status, data } = await response

        if (status === 200) {
          this.interestReceivableOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getInterestDueAndUnpaid() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 11, permission: this.permission })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.interestDueAndUnpaidOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getInterestSuspended() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 10, permission: this.permission })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.interestSuspendedOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getInterestIncome() {
      try {
        const response = await this.getGeneralLedger({
          search: { accountCategoryCode: 40304000 },
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.interestIncomeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getSpecificLoanLossAccount() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 23, permission: this.permission })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.specificLoanLossAccountOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getGeneralLoanLossAccount() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 8, permission: this.permission })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.generalLoanLossAccountOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getLoanFee() {
      try {
        const response = await this.getLoanFees({
          search: '',
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.feeDetails = data.data
          this.loanFeeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.amount}`, value: curr.code }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getUnpaidLoanFee() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 6, permission: this.permission })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.unpaidloanFeeOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getSuspendedLoanFee() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 5, permission: this.permission })
        const { status, data } = await response
        if (status === 200) {
          this.suspendedloanFeeOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async fetchPenalties() {
      try {
        const response = await this.getPenalties({
          search: '',
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.penaltyOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getSuspendedLoanPenalty() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 5, permission: this.permission })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.suspendedLoanPenaltyOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getUnpaidLoanPenalty() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 17, permission: this.permission })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.unpaidLoanPenaltyOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getPenaltyIncomeAccount() {
      try {
        const response = await this.getIncomeGLByParentCategory({ permission: this.permission })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.penaltyIncomeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async handleSubmit() {
      const action = `${this.actionType === 'add' ? this.$t('Save') : this.$t('Modify')}`
      const isFormCompleted = await this.validateOtherConfigurations()
      if (isFormCompleted) {
        const areYouSure = this.$t('Are you sure you want to')
        const loanText = this.$t('this Loan Product')
        this.confirm(this.createLoanProductAction, { title: '', text: `${areYouSure} ${action} ${loanText}?` })
      }
    },
    async createLoanProductAction() {
      const action = `${this.actionType === 'add' ? this.$t('Save') : this.$t('Modify')}`
      try {
        this.isLoading = true
        this.isDisabled = true

        const productLoanFees = this.loanProductFormData.productLoanFees !== null
          ? this.loanProductFormData.productLoanFees.map(item => {
            if (typeof item !== 'object') {
              return { loanFeeCode: item }
            }
            return { loanFeeCode: item.value }
          }) : []
        const formData = { ...this.loanProductFormData, productLoanFees }

        const response = this.actionType === 'add'
          ? await this.createLoanProduct(formData)
          : await this.updateLoanProduct(formData)

        const { status } = await response
        if (status === 200) {
          const requestToText = this.$t('Your request to')
          const text = this.$t('Loan Product was successful')
          this.alertSuccess({ text: `${requestToText} ${action} ${text}` })
          this.$router.push({ name: 'loan-product-list' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    async getLoanProductDetails() {
      try {
        const { id, type } = this.$route.params
        if (id !== undefined) {
          this.showSpinner = true
          this.showBackButton = true
          this.actionType = type
          this.isReadonly = type !== 'edit'
          this.isDisabled = type !== 'edit'
          this.isEditProductCode = true
          this.$refs.loanProduct.activateAll()

          const response = await this.getLoanProduct({ id, permission: this.permission })
          const { data, status } = await response
          if (status === 200 && data.data) {
            this.getPrincipalDueAndUnpaid(data.data.category)
            this.setLoanSizeAmount(data.data.loanSize)
            this.setApplyDefaultingLoan(data.data.applyDefaultingLoanInterest)
            this.setRepaymentType(data.data.principalRepaymentType)
            this.setInterestRepayment(data.data.interestRepaymentType)
            this.setAmountValidation(data.data.securityDepositType)
            this.setDefaultLoan(data.data.defaultingLoanInterestChargeDuration)
            this.setDiscontinuePenalty(data.data.penaltyChargeDuration)
            this.setMoratorium(data.data.setMoratorium)
            let productLoanFees

            if (data.data.productLoanFees.length) {
              const feeCodes = data.data.productLoanFees.map(item => item.loanFeeCode)
              productLoanFees = this.feeDetails.filter(items => {
                if (feeCodes.includes(items.code)) {
                  return items
                }
                return false
              }).map(item => ({ label: item.name, value: item.code }))
            }
            this.setMoratorium(data.data.allowMoratorium)
            this.loanProductFormData = {
              id,
              code: data.data.code,
              name: data.data.name,
              category: data.data.category,
              tenure: data.data.tenure,
              interestReceivableAccountNumber: data.data.interestReceivableAccountNumber,
              interestSuspenseAccountNumber: data.data.interestSuspenseAccountNumber,
              principalDueAndUnpaidAccountNumber: data.data.principalDueAndUnpaidAccountNumber,
              interestDueAndUnpaidAccountNumber: data.data.interestDueAndUnpaidAccountNumber,
              interestIncomeAccountNumber: data.data.interestIncomeAccountNumber,
              specificLoanLossAccountNumber: data.data.specificLoanLossAccountNumber,
              generalLoanLossAccountNumber: data.data.generalLoanLossAccountNumber,
              unpaidLoanFeeAccountNumber: data.data.unpaidLoanFeeAccountNumber,
              suspendedLoanFeeAccountNumber: data.data.suspendedLoanFeeAccountNumber,
              interestAccrualMode: data.data.interestAccrualMode,
              loanSize: data.data.loanSize,
              minimumLoanAmount: data.data.minimumLoanAmount,
              maximumLoanAmount: data.data.maximumLoanAmount,
              applySecurityDeposit: data.data.applySecurityDeposit,
              holdFundSecurityDeposit: data.data.holdFundSecurityDeposit,
              securityDepositType: data.data.securityDepositType,
              securityDepositCharge: data.data.securityDepositCharge || '0',
              gracePeriod: data.data.gracePeriod,
              principalRepaymentType: data.data.principalRepaymentType,
              principalFrequency: data.data.principalFrequency,
              principalToBeRepaidEvery: data.data.principalToBeRepaidEvery,
              principalInstallment: data.data.principalInstallment,
              interestRepaymentType: data.data.interestRepaymentType,
              interestFrequency: data.data.interestFrequency,
              interestToBeRepaidEvery: data.data.interestToBeRepaidEvery || '0',
              interestInstallment: data.data.interestInstallment || '0',
              defaultingInterestPercentage: data.data.defaultingInterestPercentage,
              applyLoanFees: data.data.applyLoanFees,
              productLoanFees,
              applyDefaultingLoanInterest: data.data.applyDefaultingLoanInterest,
              gracePeriodToStartDefaultInDays: data.data.gracePeriodToStartDefaultInDays || '0',
              unpaidDefaultLoanInterestAccount: data.data.unpaidDefaultLoanInterestAccount,
              suspendedDefaultLoanInterestAccount: data.data.suspendedDefaultLoanInterestAccount,
              defaultLoanInterestIncomeAccount: data.data.defaultLoanInterestIncomeAccount,
              defaultingLoanInterestChargeDuration: data.data.defaultingLoanInterestChargeDuration,
              discontinueDefaultLoanInterestChargeInDays: data.data.discontinueDefaultLoanInterestChargeInDays,
              applyPenalty: data.data.applyPenalty,
              penaltyCode: data.data.penaltyCode,
              penaltyGracePeriod: data.data.penaltyGracePeriod || '0',
              penaltyChargeDuration: data.data.penaltyChargeDuration,
              periodToDiscontinuePenalty: data.data.periodToDiscontinuePenalty || '0',
              penaltyIncomeAccountNumber: data.data.penaltyIncomeAccountNumber,
              unpaidLoanPenaltyAccountNumber: data.data.unpaidLoanPenaltyAccountNumber,
              suspendedLoanPenaltyAccountNumber: data.data.suspendedLoanPenaltyAccountNumber,
              allowMultipleActiveLoansPerAccount: data.data.allowMultipleActiveLoansPerAccount,
              allowMoratorium: data.data.allowMoratorium,
              moratoriumEffect: data.data.moratoriumEffect,
              repaymentOptionForDailyAndWeeklyHoliday: data.data.repaymentOptionForDailyAndWeeklyHoliday,
              repaymentOptionForOtherFrequencyHoliday: data.data.repaymentOptionForOtherFrequencyHoliday,
              moratoriumDuration: data.data.moratoriumDuration || '0',
              moratoriumFrequency: data.data.moratoriumFrequency,
              enableRepaymentTracker: data.data.enableRepaymentTracker,
              repaymentTrackerInDays: data.data.repaymentTrackerInDays || '0',
              allowDisbursementsToGL: data.data.allowDisbursementsToGL,
              currencyCode: data.data?.currencyCode,
              interestCode: data.data?.interestCode,
              interestRate: data.data?.interestRate,
            }
            this.showSpinner = false
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    backNavigationAction() {
      this.showBackButton = true
      this.$router.push({
        name: 'loan-product-list',
      })
    },

    // onChangeTab() {
    //   const dropDownSelect = document.querySelector('#loan-fee').querySelectorAll('.vs__selected')
    //   dropDownSelect.forEach(item => {
    //     item.classList.add('selected')
    //   })
    // },
    validateGeneral() {
      return new Promise((resolve, reject) => {
        this.$refs.generalRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validateLoanSize() {
      return new Promise((resolve, reject) => {
        this.$refs.loanSizeRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validateTenure() {
      return new Promise((resolve, reject) => {
        this.$refs.tenureRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validateInterest() {
      return new Promise((resolve, reject) => {
        this.$refs.interestRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validateReporting() {
      return new Promise((resolve, reject) => {
        this.$refs.reportingRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validateLoanFee() {
      return new Promise((resolve, reject) => {
        this.$refs.loanFeeRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validateOtherConfigurations() {
      return new Promise((resolve, reject) => {
        this.$refs.otherConfigurationRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validatePenalty() {
      return new Promise((resolve, reject) => {
        this.$refs.penaltyRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
